import { reactive } from "vue";
import { type Product, type OrderType } from "@src/api";

export type SizeList = { sizeList?: (Pick<Product, "size" | "item_id"> & { count: number })[] };
export type ItemWithSizeList = Product & SizeList;

type State = {
  type: OrderType | null;
  isLoading: boolean;
  stage: "create" | "success" | null;
  from: {
    zone: "store" | "shopfloor" | null;
    store_type: string | null;
  };
  to: {
    zone: "shopfloor" | "fittingroom" | "checkout" | null;
    section: "woman" | "man" | "kids" | null;
  };
  items: ItemWithSizeList[];
  comment: string;
  order_number: string;
};

const state = reactive<State>({
  type: "pickup",
  isLoading: false,
  stage: null,
  from: { zone: "store", store_type: null },
  to: { zone: null, section: null },
  items: [],
  comment: "",
  order_number: "",
});

export const useOrderCreate = () => {
  const reset = () => {
    state.type = "pickup";
    state.isLoading = false;
    state.stage = null;
    state.to = { zone: null, section: null };
    state.from = { zone: "store", store_type: null };
    state.comment = "";
    state.items = [];
  };

  const setFromZone = (from: "store" | "shopfloor" | null) => {
    state.from.zone = state.from.zone === from ? null : from;

    if (from === "shopfloor") state.from.store_type = null;
  };

  const setFromStoreType = (store_type: string | null) => {
    if (state.from.store_type === store_type) {
      state.from.zone = null;
      state.from.store_type = null;
    } else {
      state.from.zone = "store";
      state.from.store_type = store_type;
    }
  };

  const setToZone = (to: "shopfloor" | "fittingroom" | "checkout" | null) => {
    state.to.zone = state.to.zone === to ? null : to;
  };

  const setToSection = (section: "woman" | "man" | "kids" | null) => {
    state.to.section = state.to.section === section ? null : section;
  };

  const setType = (type: OrderType | null) => {
    state.type = type;
  };

  const toggleType = (type: OrderType) => {
    state.type = state.type === type ? null : type;
  };

  const addItem = (item: ItemWithSizeList) => {
    state.items.push(item);
  };

  const removeItem = (id: string) => {
    state.items = state.items.filter((item) => item.item_id !== id);
  };

  const clearItems = () => {
    state.items = [];
  };

  const updateItem = (id: string, itemForUpdate: ItemWithSizeList) => {
    state.items = state.items.map((item) => (item.item_id === id ? { ...item, ...itemForUpdate } : item));
  };

  const isStageCreate = () => state.stage === "create";
  const isStageSuccess = () => state.stage === "success";

  const setStage = (stage: "create" | "success") => {
    state.stage = stage;
  };

  const setLoading = (loading: boolean) => {
    state.isLoading = loading;
  };

  const changeComment = (comment: string) => {
    state.comment = comment;
  };

  const setOrderNumber = (order_number: string) => {
    state.order_number = order_number;
  };

  return {
    value: state,
    reset,
    add: addItem,
    setLoading,
    remove: removeItem,
    clear: clearItems,
    update: updateItem,
    isCreated: isStageCreate,
    isSuccess: isStageSuccess,
    setStage,
    setFromZone,
    setToZone,
    setToSection,
    setFromStoreType,
    setType,
    toggleType,
    changeComment,
    setOrderNumber,
  };
};
